import { useMemo } from 'react';
import { childrenOf, getClass } from '../../../shared/catalog/classes';
import { COSMETICS } from '../../../shared/shop';
import type { MeResponse } from '../game/api';
import { formatDate, formatNumber, t } from '../i18n';
import { className, seasonName, shopName } from '../i18n/catalog';
import { GroupPanel, type useGroup } from './GroupPanel';
import { Icon } from './icons';
import type { Page } from './MenuShell';
import { ModePicker, modeSummary, type ModeChoice } from './ModePicker';
import { usePassLevels } from './PassPage';
import { RewardArt, isMajor, rewardLabel } from './rewards';
import { TankStageView } from './TankPreview';
import { skinCodeOf } from './tankArt';

const touchFirst = () => typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

/** Hangar (lobby) : ton tank sur l'établi, nom et bouton Jouer, ordres de mission, groupe et saison. */
export function Hangar({
  me,
  name,
  onName,
  mode,
  onMode,
  onPlay,
  notice,
  onNotice,
  group,
  tooBig,
  followsLeader,
  go,
}: {
  me: MeResponse | null;
  name: string;
  onName: (s: string) => void;
  mode: ModeChoice;
  onMode: (m: ModeChoice) => void;
  onPlay: () => void;
  notice: string;
  onNotice: (s: string) => void;
  group: ReturnType<typeof useGroup>;
  tooBig: { n: number; max: number } | null;
  followsLeader: boolean;
  go: (p: Page) => void;
}) {
  const account = me?.account ?? null;
  const base = getClass('BASE');
  const look = useMemo(() => ({ code: skinCodeOf(me?.equipped) }), [me?.equipped]);
  const skin = me?.equipped ? COSMETICS.find((c) => c.id === me.equipped) : undefined;
  const firstEvo = childrenOf('BASE');
  return (
    <div className="page hangar-page">
      {notice && (
        <div className="notice" role="status">
          <p className="form-msg">{notice}</p>
          <button type="button" className="icon-btn" aria-label={t('close')} onClick={() => onNotice('')}>
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
      <div className="hangar">
        <section className="plate orders" aria-labelledby="orders-title">
          <h2 className="plate-title" id="orders-title">
            {t('hangarModes')}
          </h2>
          <ModePicker value={mode} onChange={onMode} loggedIn={!!account} />
          {tooBig && <p className="form-msg">{t('groupTooBig', { n: tooBig.n, max: tooBig.max })}</p>}
          {followsLeader && <p className="wip">{t('groupLeaderPlays')}</p>}
        </section>

        <section className="plate bench" aria-labelledby="bench-title">
          <div className="bench-head">
            <div>
              <span className="eyebrow">{t('hangarYourTank')}</span>
              <h2 className="bench-name display" id="bench-title">
                {className(base)}
              </h2>
              <p className="bench-sub">
                {skin ? t('hangarSkin', { name: shopName(skin) }) : t('hangarNoSkin')} · {t('hangarEvo', { n: firstEvo[0]?.level ?? 10, k: firstEvo.length })}
              </p>
            </div>
            <div className="menu-links">
              <button type="button" className="btn btn-small" onClick={() => go('tree')}>
                <Icon name="tree" size={16} />
                {t('classTree')}
              </button>
              <button type="button" className="btn btn-small btn-ghost" onClick={() => go('help')}>
                <Icon name="help" size={16} />
                {t('hangarControls')}
              </button>
            </div>
          </div>
          <div className="bench-floor">
            <span className="bench-corner tl" aria-hidden />
            <span className="bench-corner tr" aria-hidden />
            <span className="bench-corner bl" aria-hidden />
            <span className="bench-corner br" aria-hidden />
            <TankStageView cls={base} look={look} radius={64} label={t('hangarStageLabel', { name: className(base) })} />
            <span className="bench-hint" aria-hidden>
              {touchFirst() ? t('hangarTapHint') : t('hangarFireHint')}
            </span>
          </div>
          <form
            className="launch"
            onSubmit={(e) => {
              e.preventDefault();
              onPlay();
            }}
          >
            <label className="eyebrow" htmlFor="tank-name">
              {t('hangarCallsign')}
            </label>
            <div className="launch-row">
              <input
                id="tank-name"
                className="name-input"
                value={name}
                maxLength={16}
                placeholder={t('namePlaceholder')}
                onChange={(e) => onName(e.target.value)}
                autoComplete="nickname"
                spellCheck={false}
                autoFocus={!touchFirst()}
              />
              <button className="btn btn-primary btn-play" type="submit" data-testid="play">
                <Icon name="play" size={18} />
                <span className="play-text">
                  <span className="play-main">{account ? t('play') : t('playGuest')}</span>
                  <span className="play-sub">{modeSummary(mode)}</span>
                </span>
              </button>
            </div>
            <p className="wip">{account ? t('wipAccount') : t('wip')}</p>
          </form>
        </section>

        <div className="hangar-side">
          {account && <GroupPanel me={account} group={group} />}
          <SeasonCard me={me} go={go} />
        </div>
      </div>
    </div>
  );
}

/** Carte de saison : niveau de pass, barre d'XP et prochaine récompense marquante. */
function SeasonCard({ me, go }: { me: MeResponse | null; go: (p: Page) => void }) {
  const levels = usePassLevels();
  const pass = me?.pass;
  const level = pass?.level ?? 1;
  const next = levels?.find((l) => l.level > level && (isMajor(l.free) || isMajor(l.premium)));
  const reward = next ? (isMajor(next.free) ? next.free : next.premium) : null;
  const inLevel = (pass?.xp ?? 0) % 2000;
  const done = me?.missions ? me.missions.daily.filter((m) => m.done).length : null;
  return (
    <button type="button" className="plate tile season-card" onClick={() => go('pass')}>
      <span className="season-top">
        <span className="eyebrow">{t('seasonTag', { n: 1 })}</span>
        <span className="season-name display">{pass ? seasonName(pass.season.id, pass.season.name) : seasonName('S1', 'GENÈSE MÉCANIQUE')}</span>
      </span>
      {pass ? (
        <>
          <span className="season-level">
            <span className="season-level-n display num">{level}</span>
            <span className="season-level-of">/ 105</span>
            <span className="season-ends">{pass.ended ? t('passEnded') : t('passEnds', { d: formatDate(pass.season.endsAt) })}</span>
          </span>
          <span className="bar bar-xp" aria-hidden>
            <span className="fill fill-xp" style={{ width: `${level >= 105 ? 100 : (inLevel / 2000) * 100}%` }} />
          </span>
          <span className="season-meta num">
            {level >= 105 ? t('evolveMax') : `${formatNumber(inLevel)} / 2000 XP`}
            {done !== null && ` · ${t('seasonMissions', { n: done, max: me!.missions!.daily.length })}`}
          </span>
        </>
      ) : (
        <span className="season-meta">{t('seasonGuest')}</span>
      )}
      {next && reward && (
        <span className="season-next">
          <RewardArt r={reward} size={40} />
          <span>
            <span className="eyebrow">{t('seasonNext', { n: next.level })}</span>
            <span className="season-next-name">{rewardLabel(reward)}</span>
          </span>
        </span>
      )}
    </button>
  );
}
